import {
  createContext,
  useContext,
  useEffect,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";

/**
 * Lets a page replace the chrome's `<h1>` text with something only it knows.
 *
 * The route table in `page-titles.ts` resolves a title from the URL, which is right for
 * the ~80 routes whose title IS the route. It cannot work for the rest:
 *
 *   /corporate/:id/vendors/:profileId   the vendor's name — fetched, not in the URL
 *   /campaigns/:campaignId              the campaign's name — same
 *   /issuer-hub/prospecting             THREE titles on one route, chosen by local
 *                                       `useState` (list / create / edit)
 *
 * Before this sweep those names were the page's own `<h1>`. Moving the `<h1>` into the
 * chrome without this hook would have replaced "Cottontown Fire Dept" with the static
 * label "Vendor Details" — one title per screen, but a less useful one. The rule is
 * unchanged (the chrome owns the heading); this is only how a page hands it the words.
 *
 * `PageHeader` calls this for you, so most pages need no direct use — see its comment.
 */

/** The `useState` setter itself, so cleanup can use a functional update — see below. */
type SetPageTitleOverride = Dispatch<SetStateAction<string | null>>;

const PageTitleOverrideContext = createContext<SetPageTitleOverride | null>(null);

/** Holds the current override for one shell. Call it in the layout that renders the chrome. */
export function usePageTitleController(): {
  override: string | null;
  setOverride: SetPageTitleOverride;
} {
  const [override, setOverride] = useState<string | null>(null);
  return { override, setOverride };
}

export function PageTitleOverrideProvider({
  value,
  children,
}: {
  value: SetPageTitleOverride;
  children: ReactNode;
}) {
  return (
    <PageTitleOverrideContext.Provider value={value}>{children}</PageTitleOverrideContext.Provider>
  );
}

/**
 * Publish `title` as the chrome heading for as long as this component is mounted.
 *
 * Pass `null`/`undefined` to publish nothing and leave the route table's answer in place.
 *
 * Deliberately a no-op outside a provider: `PageHeader` renders in unit tests and on
 * public token-gated pages that have no shell, and neither should have to know about a
 * context to render a back button.
 */
export function usePageTitleOverride(title: string | null | undefined): void {
  const setOverride = useContext(PageTitleOverrideContext);

  useEffect(() => {
    if (!setOverride || !title) return;
    setOverride(title);

    return () => {
      // Clear only if this title is still the one on display. On navigation React runs
      // the outgoing page's cleanup and the incoming page's effect in the same commit,
      // and a cleanup that clears unconditionally can land after the new page has
      // already published — blanking a title that was just set correctly.
      setOverride((current) => (current === title ? null : current));
    };
    // `setOverride` is a `useState` setter, so it is referentially stable.
  }, [setOverride, title]);
}
